import React, { useCallback } from "react";
import { useCurrentProject } from "@radarly/core/project/use-cases";
import { DateMetricsCard } from "@radarly/models/cards";
import { isTimeUnitCard } from "@radarly/models/cards/is";
import { getDateFromClause } from "@radarly/models/clause/getter";
import { isDoubleTimeChartDataviz } from "@radarly/models/dataviz";
import { getPostSortFromMetric } from "@radarly/models/posts";
import { useFragment } from "../fragment";
import { useGenerateCSV } from "../hooks/useGenerateCSV";
import { CardProps } from "../model";
import DoubleTimeChart from "./double-time-chart";
import LineChart from "./line-chart";

const RadarlyMetricCards = ({
  card,
  clause,
  from,
  to,
  height,
  timezone,
  forceReload,
  openPostsPanel,
  nowDate,
}: CardProps<DateMetricsCard>) => {
  const project = useCurrentProject();
  const timeUnit = isTimeUnitCard(card) ? card.dataviz.timeUnit : undefined;
  const fragment = useFragment(
    card.fragments[0],
    clause,
    from,
    to,
    timezone,
    timeUnit
  );
  const dataviz = card.dataviz;
  const title = card.title;
  const { onGenerateCSV } = useGenerateCSV(title, fragment);

  const onClickItem = useCallback(
    (metric, range?: { from: Date; to: Date }) => {
      const { from, to } = getDateFromClause(fragment.request.query);
      openPostsPanel({
        clause: fragment.request.query,
        period: {
          from: range ? range.from : from,
          to: range ? range.to : to,
        },
        title,
        timezone,
        sortBy: getPostSortFromMetric(metric),
      });
    },
    [fragment.request.query, openPostsPanel, timezone, title]
  );

  return isDoubleTimeChartDataviz(dataviz) ? (
    <DoubleTimeChart
      dataviz={dataviz}
      title={title}
      fragment={fragment}
      project={project}
      height={height}
      onClickItem={openPostsPanel ? onClickItem : null}
      timezone={timezone}
      forceReload={forceReload}
      nowDate={nowDate}
      onGenerateCSV={onGenerateCSV}
    />
  ) : (
    <LineChart
      fragment={fragment}
      project={project}
      dataviz={dataviz}
      title={title}
      height={height}
      onClickItem={openPostsPanel ? onClickItem : null}
      timezone={timezone}
      forceReload={forceReload}
      nowDate={nowDate}
      onGenerateCSV={onGenerateCSV}
    />
  );
};

export default RadarlyMetricCards;
